'use client';

import { CustomImage as Image } from '..';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  ShopProductCardRoot,
  ShopCardBody,
  ShopCardBottomSheet,
  ShopCardFooterRow,
  ShopCardMedia,
  ShopCardMediaDimBackdrop,
  ShopCardPriceCluster,
  ShopCardPrimaryCta,
  ShopCardSpecSection,
  ShopCardTag,
  ShopCardFlipImg,
  ShopCardTagText,
} from './shopProductCard';
import styles from './ProductCardAccessories.module.css';
import { useCartStore, useShallow } from '..';
import { useTranslations } from 'next-intl';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { getTaxString } from 'components/shared/Cart/utils';
import { useClickOutside } from 'hooks/dom/useClickOutside';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import {
  CurrencySymbol,
  getCurrencySymbolFromEnum,
  useFormatPrice,
} from 'hooks/pricing/useFormattedPrice';
import { TravelChargerShopifyMap } from 'content/products/accessories';
import { ProductType } from 'components/shared/Cart/types';
import { Currency } from 'utils/Currency';
import { DiscountPill, DotSpinner } from 'components/shared';
import { RingSizes } from 'content/products/ring';
// import { ProductPurchaseType } from 'content/products';

export const ProductCardAccessories = ({
  className,
  id,
  imgSrc,
  hoverImgSrc,
  productType,
  data,
  title,
  description,
  tag,
  mainImgFit,
  originalPrice,
  discountPercentage,
  onPrimaryCta,
  primaryCtaLabel,
}: {
    className?: string;
    id?: string;
    imgSrc?: string;
    hoverImgSrc?: string;
    productType: ProductType;
    data: Record<ShopifyStore, Record<string, string | number>>;
    title: string;
    description?: string;
    tag?: 'new' | 'popular' | 'sold out';
    mainImgFit?: 'cover' | 'contain';
    originalPrice?: string;
    discountPercentage?: string;
    /** When set, primary CTA runs this instead of add-to-cart (e.g. gift card). */
    onPrimaryCta?: () => void;
    primaryCtaLabel?: string;
  }) => {
    const CartClient = useCartStore(
      useShallow((s) => ({
        store: s.store,
        taxString: s.taxString,
        addToCart: s.addToCart,
        currency: s.currency,
      })),
    );
    const { store: regionStore } = useRegionLocale();
    const resolvedStore = (CartClient.store ?? regionStore) as ShopifyStore;
    const tCart = useTranslations('cart');
    const resolvedTaxString =
      CartClient.taxString || getTaxString(resolvedStore, tCart);
    const [flipCard, setFlipCard] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const formatPriceWithComma = useFormatPrice();

    const handlePrimaryCta = async () => {
      if (onPrimaryCta) {
        onPrimaryCta();
        return;
      }

      const variantId = data?.[CartClient.store as ShopifyStore]
        .shopifyVariantId as string;

      try {
        setIsLoading(true);
        await CartClient.addToCart([
          {
            shopifyVariantId: variantId,
            productType: productType,
          },
        ]);
      } finally {
        setIsLoading(false);
      }
    };

    return (
      <ShopProductCardRoot
        className={className}
        id={id}
        onPointerOver={() => setFlipCard(true)}
        onPointerOut={() => setFlipCard(false)}
      >
        <ShopCardMedia as="div" $background="transparent">
          {imgSrc && (
            <Image
              src={imgSrc}
              alt={title}
              height={448}
              width={300}
              style={{
                objectFit: mainImgFit ?? 'cover',
                width: '100%',
                borderRadius: '12px',
              }}
            />
          )}
          {hoverImgSrc && (
            <ShopCardFlipImg>
              <Image
                src={hoverImgSrc}
                id="productImg"
                alt={title}
                height={456}
                width={300}
                style={{
                  position: 'absolute',
                  width: '100%',
                  objectFit: 'cover',
                  top: '0',
                  left: '0',
                  opacity: flipCard ? '1' : '0',
                  transition: 'opacity 0.3s ease-out',
                }}
              />
            </ShopCardFlipImg>
          )}
        </ShopCardMedia>
        <ShopCardBody>
          <ShopCardSpecSection className="acessSpec">
            <h3 className="title">{title}</h3>
            {description && <p className="description">{description}</p>}
            <br />
          </ShopCardSpecSection>
          <ShopCardFooterRow>
            <ShopCardPriceCluster className="pricing-info">
              {originalPrice && discountPercentage && (
                <span className="striked">
                  {
                    CurrencySymbol[
                      (CartClient.currency ?? Currency.usd) as Currency
                    ]
                  }
                  {formatPriceWithComma(parseFloat(originalPrice))}
                </span>
              )}
              {
                CurrencySymbol[
                  (CartClient.currency ?? Currency.usd) as Currency
                ]
              }
              {formatPriceWithComma(
                (() => {
                  const originalPrice =
                    (data?.[resolvedStore]?.price as number) ?? 0;
                  if (originalPrice && discountPercentage) {
                    return (
                      originalPrice * (1 - parseFloat(discountPercentage) / 100)
                    );
                  }
                  return originalPrice;
                })(),
              )}
              <span>{resolvedTaxString}</span>
            </ShopCardPriceCluster>
            <ShopCardPrimaryCta
              className={
                tag === 'sold out' || isLoading
                  ? 'quickAdd disable'
                  : 'quickAdd'
              }
              onClick={handlePrimaryCta}
              data-buttontype={onPrimaryCta ? 'gift card cta' : 'add to cart'}
              data-product={title}
              disabled={tag === 'sold out' || isLoading}
              aria-busy={isLoading}
              data-variantId={
                CartClient.store
                  ? data?.[CartClient.store as ShopifyStore].shopifyVariantId
                  : ''
              }
            >
              {isLoading ? (
                <DotSpinner color="#FFFFFF" />
              ) : (
                primaryCtaLabel ?? tCart('sharedCart.upsell.addToCart')
              )}
            </ShopCardPrimaryCta>
          </ShopCardFooterRow>
        </ShopCardBody>
        {tag ? (
          <ShopCardTag $heightMode="accessories"><ShopCardTagText tag={tag} /></ShopCardTag>
        ) : null}
      </ShopProductCardRoot>
    );
};

export const ProductCardTravelCharger = ({
    className,
    imgSrc,
    hoverImgSrc,
    sizing = false,
    productType,
    tag,
    mainImgFit,
    showId = false,
    discountPercentage,
    offerLabel,
    data,
    title,
    description,
    hideOpenSize = false,
    sizeSubtitle,
    unavailableSizes,
    variants,
  }: {
    className?: string;
    imgSrc?: string;
    hoverImgSrc?: string;
    sizing?: boolean;
    isMerch?: boolean;
    productType: ProductType;
    tag?: string;
    mainImgFit?: 'cover' | 'contain';
    showId?: boolean;
    discountPercentage?: string;
    offerLabel?: string;

    data?: Record<ShopifyStore, Record<string, any>>;
    title?: string;
    description?: string;
    hideOpenSize?: boolean;
    sizeSubtitle?: string;
    unavailableSizes?: RingSizes[];
    variants?: {
      label: string;

      data: Record<ShopifyStore, Record<string, any>>;
    }[];
  }) => {
    const CartClient = useCartStore(
      useShallow((s) => ({
        store: s.store,
        taxString: s.taxString,
        addToCart: s.addToCart,
        currency: s.currency,
      })),
    );
    const { store: regionStore } = useRegionLocale();
    const resolvedStore = (CartClient.store ?? regionStore) as ShopifyStore;
    const tCart = useTranslations('cart');
    const t = useTranslations('ring');
    const resolvedTaxString =
      CartClient.taxString || getTaxString(resolvedStore, tCart);
    const formatPriceWithComma = useFormatPrice();
    const [showSizes, setShowSizes] = useState(false);
    const [flipCard, setFlipCard] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const [selectedSize, setSelectedSize] = useState<RingSizes | null>(null);
    const [selectedVariant, setSelectedVariant] = useState<number | null>(null);
    const [step, setStep] = useState<'variant' | 'size'>('variant');

    const ref = useRef<HTMLDivElement>(null);

    const hasVariants = variants && variants.length > 0;

    const cardBasePrice = hasVariants
      ? Math.min(
          ...variants.map(
            (v) => (v.data[resolvedStore]?.price as number) ?? 0,
          ),
        )
      : ((data || TravelChargerShopifyMap)?.[resolvedStore]?.price as number) ??
        0;
    const cardDisplayPrice =
      cardBasePrice > 0 && discountPercentage
        ? cardBasePrice * (1 - parseFloat(discountPercentage) / 100)
        : cardBasePrice;

    const activeData =
      hasVariants && selectedVariant !== null
        ? variants[selectedVariant].data
        : data;

    const hoverOut = useCallback(() => {
      if (showSizes) {
        setTimeout(() => {
          setSelectedSize(null);
          setSelectedVariant(null);
          setStep('variant');
          setShowSizes(false);
        }, 200);
      }
    }, [showSizes]);

    useClickOutside(ref, hoverOut, showSizes);

    const handleAddToCart = async () => {
      const shopifyMap = activeData || data || TravelChargerShopifyMap;
      const variantId =
        shopifyMap[CartClient.store as ShopifyStore]?.[
          selectedSize as RingSizes
        ]?.shopifyVariantId;

      if (!variantId) return;

      try {
        setIsLoading(true);
        await CartClient.addToCart([
          {
            shopifyVariantId: variantId,
            productType: productType,
          },
        ]);
      } finally {
        setIsLoading(false);
        hoverOut();
      }
    };

    //reset
    useEffect(() => {
      setSelectedSize(null);
    }, [showSizes]);

    return (
      <ShopProductCardRoot
        className={[styles.root, className].filter(Boolean).join(' ')}
        onPointerOver={() => hoverImgSrc && setFlipCard(true)}
        onPointerOut={() => hoverImgSrc && setFlipCard(false)}
        id={showId ? 'voyager-charger' : 'charger'}
      >
        <ShopCardMedia as="div" $background="white">
          {imgSrc && (
            <Image
              src={imgSrc}
              alt={title || t('ring.shop.products.voyager.title')}
              height={448}
              width={300}
              style={{
                objectFit: mainImgFit ?? 'cover',
                width: '100%',
                borderRadius: '12px',
              }}
            />
          )}
          {hoverImgSrc && (
            <ShopCardFlipImg>
              <Image
                src={hoverImgSrc}
                id="productImg"
                alt={title || t('ring.shop.products.voyager.title')}
                height={456}
                width={300}
                style={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  top: '0',
                  left: '0',
                  opacity: flipCard ? '1' : '0',
                  transition: 'opacity 0.3s ease-out',
                }}
              />
            </ShopCardFlipImg>
          )}
        </ShopCardMedia>
        <ShopCardBody>
          <ShopCardSpecSection className="ringSpec">
            <h3 className="title">
              {title || t('ring.shop.products.voyager.title')}
            </h3>
            <p className="description">
              {description || t('ring.shop.products.voyager.description')}
            </p>
            <br />
          </ShopCardSpecSection>
          <ShopCardFooterRow
            aria-hidden={showSizes}
            style={showSizes ? { visibility: 'hidden' } : undefined}
          >
            <ShopCardPriceCluster className="pricing-info">
              {hasVariants && (
                <span style={{ flexBasis: '100%' }}>
                  {tCart('sharedCart.upsell.startsFrom')}
                </span>
              )}
              {discountPercentage && cardBasePrice > 0 && (
                <span className="striked">
                  {
                    CurrencySymbol[
                      (CartClient.currency ?? Currency.usd) as Currency
                    ]
                  }
                  {formatPriceWithComma(cardBasePrice)}
                </span>
              )}
              {
                CurrencySymbol[
                  (CartClient.currency ?? Currency.usd) as Currency
                ]
              }
              {formatPriceWithComma(cardDisplayPrice)}
              <span>{resolvedTaxString}</span>
              {offerLabel && (
                <span
                  className={styles.offerPillWrap}
                  style={{ flexBasis: '100%' }}
                >
                  <DiscountPill className={styles.offerPill}>
                    {offerLabel}
                  </DiscountPill>
                </span>
              )}
            </ShopCardPriceCluster>
            <ShopCardPrimaryCta
              data-buttontype="show size"
              className={tag === 'sold out' ? 'quickAdd disable' : 'quickAdd'}
              onClick={() => tag !== 'sold out' && setShowSizes(true)}
              disabled={tag === 'sold out'}
            >
              {tCart('sharedCart.upsell.addToCart')}
            </ShopCardPrimaryCta>
          </ShopCardFooterRow>
        </ShopCardBody>
        {tag ? <ShopCardTag><ShopCardTagText tag={tag} /></ShopCardTag> : null}
        {sizing && (
          <ShopCardMediaDimBackdrop className={showSizes ? 'open' : ''} />
        )}
        {sizing && (
          <ShopCardBottomSheet
            className={`sizeOverlay ${showSizes ? 'open' : ''} ${styles.bottomSheet}`}
            ref={ref}
            aria-hidden={!showSizes}
          >
            {hasVariants && step === 'variant' ? (
              <div className="variantSelect">
                <div className="variantOptions">
                  {variants.map((variant, idx) => {
                    const price =
                      (variant.data[resolvedStore]?.price as number) ?? 0;
                    const displayPrice =
                      price && discountPercentage
                        ? price * (1 - parseFloat(discountPercentage) / 100)
                        : price;
                    return (
                      <button
                        key={idx}
                        className="variantBtn"
                        onClick={() => {
                          setSelectedVariant(idx);
                          setStep('size');
                        }}
                      >
                        <span className="variantLabel">{variant.label}</span>
                        <span className="variantPrice">
                          {displayPrice < price && (
                            <span className="striked">
                              {
                                CurrencySymbol[
                                  (CartClient.currency ??
                                    Currency.usd) as Currency
                                ]
                              }
                              {formatPriceWithComma(price)}
                            </span>
                          )}
                          {
                            CurrencySymbol[
                              (CartClient.currency ?? Currency.usd) as Currency
                            ]
                          }
                          {formatPriceWithComma(displayPrice)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <>
                <div className="size">
                  <div className="sizeHead">
                    {hasVariants && (
                      <button
                        className="overlayBack"
                        aria-label={t('packs.back')}
                        onClick={() => {
                          setSelectedSize(null);
                          setStep('variant');
                        }}
                      >
                        ←
                      </button>
                    )}
                    <div className="heading">
                      {tCart('sharedCart.upsell.size')}
                    </div>
                  </div>
                  {sizeSubtitle && (
                    <p className="sizeSubtitle">{sizeSubtitle}</p>
                  )}
                  <div className="sizes">
                    {Object.keys(RingSizes)
                      .slice(0, Object.keys(RingSizes).length / 2)
                      .filter((item) => !(hideOpenSize && item === '99'))
                      .map((item, index) => (
                        <button
                          className={`gtm ${
                            selectedSize === (item as unknown as RingSizes)
                              ? 'selectedRingSize'
                              : ''
                          }`}
                          key={index}
                          data-buttontype="select size"
                          disabled={
                            unavailableSizes?.includes(
                              Number(item) as RingSizes,
                            ) ?? false
                          }
                          onClick={() =>
                            setSelectedSize(item as unknown as RingSizes)
                          }
                          aria-pressed={
                            selectedSize === (item as unknown as RingSizes)
                          }
                        >
                          {item === '99'
                            ? t('ring.shop.optedForRingSizingKit')
                            : item}
                        </button>
                      ))}
                  </div>
                </div>

                <button
                  className="addToCart"
                  data-buttontype="add to cart"
                  onClick={handleAddToCart}
                  disabled={!selectedSize || isLoading}
                  aria-busy={isLoading}
                >
                  {isLoading ? (
                    <DotSpinner color="#FFFFFF" />
                  ) : (
                    <>
                      {tCart('sharedCart.upsell.addToCartFor', {
                        price: `${getCurrencySymbolFromEnum(
                          (CartClient.currency ?? Currency.usd) as Currency,
                        )}${formatPriceWithComma(
                          (() => {
                            const priceSource =
                              activeData || data || TravelChargerShopifyMap;
                            const originalPrice =
                              (priceSource?.[resolvedStore]?.price as number) ??
                              0;
                            if (originalPrice && discountPercentage) {
                              return (
                                originalPrice *
                                (1 - parseFloat(discountPercentage) / 100)
                              );
                            }
                            return originalPrice;
                          })(),
                        )}`,
                      })}
                    </>
                  )}
                </button>
              </>
            )}
          </ShopCardBottomSheet>
        )}
      </ShopProductCardRoot>
    );
};
